import toasty from "./toasty";
import { callAPI } from "../hooks/useApi";

/**
 * No-op callback used as a safe default.
 */
export function noop() {}
export function csvFilename(name) {
	const sanitized = name.replace(/\s+/g, "_").toLowerCase();
	return `warrisk-${sanitized}-${new Date().toISOString().split("T")[0]}.csv`;
}

/**
 * Normalizes metadata fields by parsing dates and JSON user payloads in place.
 */
export function parseMeta(n) {
	if (n.created) {
		n.created =
			typeof n.created === "string" ? new Date(n.created) : n.created;
	}
	if (n.created_by) {
		n.created_by = toObject(n.created_by);
	}
	if (n.updated) {
		n.updated =
			typeof n.updated === "string" ? new Date(n.updated) : n.updated;
	}
	if (n.updated_by) {
		n.updated_by = toObject(n.updated_by);
	}
	if (n.deleted_at) {
		n.deleted_at =
			typeof n.deleted_at === "string"
				? new Date(n.deleted_at)
				: n.deleted_at;
	}
	if (n.deleted_by) {
		n.deleted_by = toObject(n.deleted_by);
	}
	return n;
}

/**
 * Formats a Date as ISO-8601 without timezone suffix.
 */
export function toISOwotz(date = new Date(0)) {
	if (typeof date === "string") {
		date = new Date(date);
	}
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
		return null;
	}
	// toISO without timezone
	const year = date.getFullYear();
	const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
	const day = date.getDate().toString().padStart(2, "0");
	const hours = date.getHours().toString().padStart(2, "0");
	const minutes = date.getMinutes().toString().padStart(2, "0");
	const seconds = date.getSeconds().toString().padStart(2, "0");
	const milliseconds = date.getMilliseconds().toString().padStart(3, "0");

	return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
}

/**
 * Builds an inclusive PostgREST date range clause for a filter key.
 */
export function buildDateRangeClause(key, value) {
	if (!value?.from || !value?.to) {
		return undefined;
	}

	const from = toISOwotz(value.from);
	const to = toISOwotz(value.to);
	if (!from || !to) {
		return undefined;
	}

	return `(${key}.gte.${from},${key}.lte.${to})`;
}

/**
 * Formats a date-like value with local date and time.
 */
export const formatDateTime = (value) =>
	value ? new Date(value).toLocaleString() : "N/A";
/**
 * Formats a date-like value with local date only.
 */
export const formatDate = (value) =>
	value ? new Date(value).toLocaleDateString() : "N/A";
/**
 * Formats a date-like value with local time only.
 */
export const formatTime = (value) =>
	value ? new Date(value).toLocaleTimeString() : "N/A";

/**
 * Formats a number as USD currency.
 */
export function formatUSD(value, decimals = 0) {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		maximumFractionDigits: decimals,
	}).format(value);
}
/**
 * Formats a number using for en-US grouping.
 */
export function formatNumber(value) {
	return new Intl.NumberFormat("en-US").format(value);
}
/**
 * Formats a number as currency for a provided currency code.
 */
export function formatCurrency(value, currency = "USD", decimals = 0) {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency,
		maximumFractionDigits: decimals,
	}).format(value);
}

/**
 * Converts a numeric value to compact units (K/M/B/T).
 */
export function compactCurrency(number, decimals = 0, unit = null) {
	const dict = [
		[1e12, "T"],
		[1e9, "B"],
		[1e6, "M"],
		[1e3, "K"],
		[1, ""],
		[0, ""],
	];

	const t = unit
		? dict.find((i) => unit === i[1])
		: dict.find((i) => number >= i[0]);

	if (!t)
		throw new Error(
			`compact_currency: wrong set of arguments: ${number}, ${decimals}, ${unit}`
		);

	if (number === 0) return "0";

	return (
		(number / t[0])
			.toFixed(decimals)
			.replace(/\.0+$|(\.[0-9]*[1-9])0+$/, "$1") + t[1]
	);
}
/**
 * Generates a random UUID v4-like identifier.
 */
export function genuuid() {
	const r = (c) =>
		(
			c ^
			(crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
		).toString(16);
	return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, r);
}

/**
 * Performs deep equality checks for primitives, arrays, and objects.
 */
export function deepEqual(a, b) {
	if (a === b) return true;
	if (a === null || b === null) return false;
	if (typeof a !== typeof b) return false;
	if (typeof a !== "object") return a === b;
	if (Array.isArray(a) && Array.isArray(b)) {
		return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
	}
	if (Object.keys(a).length !== Object.keys(b).length) return false;
	return Object.keys(a).every((k) => deepEqual(a[k], b[k]));
}

/**
 * Returns true when a value is an empty plain object.
 */
export function isEmpty(obj) {
	return (
		obj &&
		Object.keys(obj).length === 0 &&
		Object.getPrototypeOf(obj) === Object.prototype
	);
}

/**
 * Parses JSON strings to objects and returns objects unchanged.
 */
export function toObject(strOrObj) {
	if (!strOrObj) return null;
	if (typeof strOrObj === "string") {
		try {
			return JSON.parse(strOrObj);
		} catch {
			console.warn("toObject: failed to parse string:", strOrObj);
			return null;
		}
	}
	return strOrObj;
}

/**
 * Converts an object of parallel arrays into an array of row objects.
 */
export function objToArr(obj) {
	if (!obj || Object.keys(obj).length === 0) {
		return [];
	}
	const keys = Object.keys(obj);
	const length = Math.max(...keys.map((key) => obj[key].length));

	return Array.from({ length }, (_, i) =>
		Object.fromEntries(keys.map((key) => [key, obj[key][i]]))
	);
}

/**
 * Picks only the requested keys from an object.
 */
export const filterKeys = (keys, data) => {
	if (!data) {
		return {};
	}
	return Object.keys(data).reduce((a, b) => {
		if (keys.includes(b)) {
			a[b] = data[b];
		}
		return a;
	}, {});
};
/**
 * Removes entries that match a provided value.
 */
export function filterValues(data, value = null) {
	return Object.fromEntries(
		Object.entries(data).filter(([_, v]) => v !== value)
	);
}

/**
 * Resolves a value that can be a literal or a function.
 */
export const strOrFn = (v, ...args) =>
	typeof v === "function" ? v(...args) : v;

/**
 * Returns the live browser search string when available for safe query merges.
 */
export function getCurrentSearch(fallbackSearch = "") {
	return typeof window !== "undefined"
		? window.location.search
		: fallbackSearch;
}

/**
 * Safely traverses nested values using a path list.
 */
export function maybe(o, ...path) {
	return o === null || o === undefined || !path.length
		? o
		: maybe(o[path[0]], ...path.slice(1));
}
/**
 * Reads a value by key or dot-path from an object.
 */
export function getMaybe(a, data) {
	return a?.match(".*\\..*") ? maybe(data, ...a.split(".")) : data[a];
}

/**
 * Debounces function execution until calls stop for the timeout period.
 */
export function debounce(fn, timeout = 300) {
	let t;

	return (...args) => {
		clearTimeout(t);
		t = setTimeout((_) => fn.apply(this, args), timeout);
	};
}

/**
 * Delays execution for a specified number of milliseconds.
 */
export function delay(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Triggers a browser download from a Blob, Promise<Blob>, or string content.
 */
export async function downloadBlob(
	blob,
	filename,
	mimeType = "application/octet-stream"
) {
	//if(blob is promise)
	if (blob instanceof Promise) {
		blob = await blob;
	} else if (typeof blob === "string") {
		blob = new Blob([blob], { type: mimeType });
	}
	const url = window.URL.createObjectURL(blob, { type: mimeType });
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", filename);
	document.body.appendChild(link);
	link.click();
	link.parentNode.removeChild(link);
	window.URL.revokeObjectURL(url);
}

/**
 * Downloads a file from S3 using a signed URL endpoint.
 */
export async function s3Download(
	dir,
	id,
	name,
	type = "application/pdf",
	token
) {
	if (!dir || !id) {
		toasty.error("Invalid file parameters");
		return;
	}
	const u = await fetch(
		`${S3_SIGNER_URL}/s3-presigned-get?directory=${dir}&file-id=${id}`,
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	)
		.then((r) => (r.ok ? r.text() : null))
		.then((s3url) => {
			if (!s3url) return null;

			if (location.hostname === "localhost") {
				return s3url;
			} else if (AWS_S3_PROXY) {
				const orig = new URL(s3url);
				const rewrite = new URL(
					AWS_S3_PROXY + orig.pathname + orig.search,
					BASE_URL
				);
				return rewrite;
			} else {
				return s3url;
			}
		});

	if (!u) {
		toasty.error("Failed to fetch file's location");
		return;
	}
	return fetch(u).then((r) => {
		if (!r.ok) {
			if (r.status === 404) {
				toasty.error("File not found");
			} else if (r.status === 403) {
				toasty.error("Access denied to file");
			} else if (r.status === 500) {
				toasty.error("Server error while fetching file");
			} else {
				toasty.error(`Failed to fetch file (${r.status})`);
			}
			return;
		}
		return downloadBlob(r.blob(), name, type);
	});
}

/**
 * Uploads a file to S3 and optionally registers metadata in the API.
 */
export async function s3Upload(
	id,
	dir,
	file,
	callback = (_id, _file) => {},
	onProgress = null,
	onError = null,
	token
) {
	const u = await fetch(
		`${S3_SIGNER_URL}/s3-presigned-put?directory=${dir}&file-id=${id}`,
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		}
	).then((r) => (r.ok ? r.text() : null));

	if (!u) {
		toasty.error("Failed to create file's location");
		throw new Error("Failed to create file's location");
	}

	const reader = new FileReader();
	reader.addEventListener("load", (e) => {
		const xhr = new XMLHttpRequest();

		if (onProgress) {
			xhr.upload.addEventListener("progress", (event) => {
				if (event.lengthComputable) {
					onProgress(event.loaded / event.total);
				}
			});
		}
		xhr.addEventListener("loadend", async () => {
			// register file in db
			const success = xhr.readyState === 4 && xhr.status === 200;
			if (!success) {
				onError?.(
					id,
					new Error(`Error uploading file (status ${xhr.status})`)
				);
				onProgress?.(1);
				return;
			}

			callback(id, file, {
				id,
				filename: file.name,
				folder: dir,
			});
			onProgress?.(1);
		});

		xhr.open("PUT", u, true);
		xhr.send(e.target.result);
	});

	reader.readAsArrayBuffer(file);

	return id;
}

/**
 * Archives one or more files.
 */
export const archiveFiles = async (files, token) => {
	if (!token) {
		throw new Error("Authentication token is required to archive files.");
	}
	try {
		const uniqueFiles = files.filter((v, i, a) => a.indexOf(v) === i);
		if (uniqueFiles.length === 0) {
			toasty.error("No files to archive");
			return;
		}		
		const _res = await callAPI(
			"rpc/files_archive_create",
			{
				method: "POST",
				params: { files: uniqueFiles },
				schema: "portal",
			},
			token
		);

		toasty.success(
			`Compressing ${uniqueFiles.length} files - it will be available under notifications when ready.`
		);
	} catch (e) {
		toasty.error("Error creating archive", e);
		return;
	}
};

/**
 * Quotes a cell value for CSV export.
 */
export const quoteCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

/**
 * Unquotes a CSV cell value, handling escaped quotes and trimming whitespace.
 */
export const unquoteCell = (raw) => {
	const v = raw.trim();
	return v.startsWith('"') && v.endsWith('"')
		? v.slice(1, -1).replace(/""/g, '"')
		: v;
};

/**
 * Pads CSV rows so all rows have equal column lengths.
 */
function sanitizeLengths(rows, maxRowLength) {
	return rows.map((row) => {
		const rLength = row.split(";").length;
		if (rLength < maxRowLength) {
			const diff = maxRowLength - rLength;
			row += ";".repeat(diff);
		}
		return row;
	});
}

/**
 * Converts row objects into CSV and downloads the file.
 */
export function downloadCSV(name, header, rows) {
	const content = [header.join(";")].concat(
		rows.map((row) =>
			Object.keys(row)
				.map((k) =>
					row[k] === null
						? ""
						: Array.isArray(row[k]) || typeof row[k] === "object"
							? JSON.stringify(row[k])
							: row[k] || ""
				)
				.join(";")
		)
	);
	const maxRowLength = content.reduce(
		(acc, row) => Math.max(acc, row.split(";").length),
		0
	);
	return downloadBlob(
		sanitizeLengths(content, maxRowLength).join("\n"),
		name.replace(" ", "-").toLowerCase() +
			"-" +
			new Date().toISOString().split("T")[0] +
			".csv",
		"text/csv"
	);
}

/**
 * Converts a string list to {label, value} option objects.
 */
export function stringToOptions(str, lowerCase = true) {
	return str.map((s) => {
		return {
			label: s,
			value: lowerCase
				? s.replaceAll(" ", "_").toLowerCase()
				: s.replaceAll(" ", "_"),
		};
	});
}

/**
 * Converts a key-value object to an options list.
 */
export function objectToOptionsList(obj) {
	const result = Object.keys(obj).map((key) => {
		return { label: obj[key], value: key };
	});

	return result;
}

/**
 * Converts FormData into a plain object and groups [] keys as arrays.
 */
export const formDataToObject = (formData) => {
	const result = {};

	for (const [key, value] of formData.entries()) {
		// handle arrays
		if (key.endsWith("[]")) {
			const arrayKey = key.slice(0, -2);
			if (!result[arrayKey]) {
				result[arrayKey] = [];
			}
			result[arrayKey].push(value);
		} else {
			result[key] = value;
		}
	}

	return result;
};

/**
 * Deduplicates array items using a custom comparator.
 */
export const unique = (arr, compareFn) => {
	return arr.filter(
		(item, index, self) =>
			index === self.findIndex((t) => compareFn(item, t))
	);
};

/**
 * Replaces double spaces with single spaces.
 */
export const sanitizeSpaces = (value) => {
	return value.replaceAll("  ", " ");
};

/**
 * Parses markdown style links [label](url) and converts them to HTML anchor elements.
 */

export const parseUrl = (text) => {
	if (typeof text !== "string" || !text.length) {
		return text;
	}

	const parts = [];
	let cursor = 0;

	while (cursor < text.length) {
		const labelStart = text.indexOf("[", cursor);
		if (labelStart === -1) {
			parts.push(text.slice(cursor));
			break;
		}

		const labelEnd = text.indexOf("]", labelStart + 1);
		if (labelEnd === -1 || text[labelEnd + 1] !== "(") {
			parts.push(text.slice(cursor, labelStart + 1));
			cursor = labelStart + 1;
			continue;
		}

		let urlEnd = labelEnd + 2;
		let depth = 1;
		while (urlEnd < text.length && depth > 0) {
			if (text[urlEnd] === "(") depth += 1;
			if (text[urlEnd] === ")") depth -= 1;
			urlEnd += 1;
		}

		if (depth !== 0) {
			parts.push(text.slice(cursor));
			break;
		}

		if (labelStart > cursor) {
			parts.push(text.slice(cursor, labelStart));
		}

		const label = text.slice(labelStart + 1, labelEnd);
		const href = text.slice(labelEnd + 2, urlEnd - 1);
		parts.push(
			<a
				key={parts.length}
				href={href}
				target="_blank"
				rel="noopener noreferrer"
				className="text-primary underline"
			>
				{label}
			</a>
		);
		cursor = urlEnd;
	}

	return parts.length === 1 && typeof parts[0] === "string"
		? parts[0]
		: parts;
};
